import React, { useCallback, useMemo, useState } from 'react';
import { Modal, ModalHeader, ModalTitle, ModalContent, ModalFooter } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import type { EmailTemplateDocument, EmailTemplateFolder } from '../useData';
import TemplatesLibraryPanel from './TemplatesLibraryPanel';

export interface AttachFromTemplateLibraryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAttach: (documents: EmailTemplateDocument[]) => Promise<void>;
  companyDocuments: EmailTemplateDocument[];
  personalDocuments: EmailTemplateDocument[];
  companyFolders: EmailTemplateFolder[];
  personalFolders: EmailTemplateFolder[];
  companyCanEdit?: boolean;
  isLoading?: boolean;
}

const AttachFromTemplateLibraryModal: React.FC<AttachFromTemplateLibraryModalProps> = ({
  isOpen,
  onClose,
  onAttach,
  companyDocuments,
  personalDocuments,
  companyFolders,
  personalFolders,
  companyCanEdit = false,
  isLoading,
}) => {
  const { colors } = useTheme();
  const [selected, setSelected] = useState<Set<number>>(() => new Set());
  const [attaching, setAttaching] = useState(false);

  const allDocuments = useMemo(
    () => [...companyDocuments, ...personalDocuments],
    [companyDocuments, personalDocuments],
  );

  const toggleDocument = useCallback((documentId: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(documentId)) next.delete(documentId);
      else next.add(documentId);
      return next;
    });
  }, []);

  const handleAttach = async () => {
    const docs = allDocuments.filter((d) => selected.has(d.id));
    if (!docs.length) return;
    setAttaching(true);
    try {
      await onAttach(docs);
      setSelected(new Set());
      onClose();
    } finally {
      setAttaching(false);
    }
  };

  const handleClose = () => {
    if (attaching) return;
    setSelected(new Set());
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl">
      <ModalHeader>
        <ModalTitle>Attach from library</ModalTitle>
        <button
          type="button"
          onClick={handleClose}
          className={cn('p-1.5 rounded-lg', colors.textMuted, colors.interactiveHover)}
          aria-label="Close"
          disabled={attaching}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </ModalHeader>
      <ModalContent>
        <p className={cn('text-sm mb-3 -mt-1', colors.textMuted)}>
          Browse folders on the left, select reference documents (PDF, Word, Excel), then attach.
        </p>
        <div className={cn('-mx-6 -mb-6 h-[min(68vh,640px)] flex flex-col min-h-0', attaching && 'pointer-events-none opacity-70')}>
          <TemplatesLibraryPanel
            pickerMode="document"
            company={{
              folders: companyFolders,
              templates: [],
              documents: companyDocuments,
              canEdit: companyCanEdit,
            }}
            personal={{
              folders: personalFolders,
              templates: [],
              documents: personalDocuments,
            }}
            shared={{ folders: [], templates: [] }}
            isLoading={isLoading}
            selectedDocumentIds={selected}
            onToggleDocument={toggleDocument}
            onImported={() => undefined}
            onError={() => undefined}
            onSuccess={() => undefined}
          />
        </div>
      </ModalContent>
      <ModalFooter>
        <Button type="button" variant="secondary" onClick={handleClose} disabled={attaching}>
          Cancel
        </Button>
        <Button
          type="button"
          onClick={handleAttach}
          disabled={attaching || selected.size === 0}
          loading={attaching}
        >
          Attach {selected.size > 0 ? `(${selected.size})` : ''}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AttachFromTemplateLibraryModal;
